"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { connectWallet, getWalletOpenUrl, WalletConnectorError, type Eip1193Provider, type WalletConnection } from "@/features/auth/wallet/connectors";
import type { ConfirmFinanceStepPayload, FinanceSignPayload, FinanceStepResponse } from "../types";
import { AccountMismatchError, MissingSignPayloadError, NetworkMismatchError, TransactionFailedError, UnknownFinanceStepError, runExternalFinanceSigning, type FinanceSigningResult, type FinanceSigningStatus, type SignRequiredStep } from "../external-signing";

/**
 * React binding for `external-signing.ts` — the browser half: the connector,
 * the provider, ethers, and the UI state around them.
 *
 * Deliberately the same shape as `features/wallet/hooks/useExternalWithdraw`
 * (same two wallet targets, same lazy `BrowserProvider` import, same
 * single-flight lock, same locale-aware messages) so the asset page's
 * cancel/accept paths behave exactly like the wallet page's withdraw path in
 * front of the user. The one structural difference is the send guard: it is
 * keyed PER ROUND, because accepting an offer legitimately sends two
 * transactions while still never sending either of them twice.
 */

/** This flow only ever offers the two injected wallets, as withdraw does. */
export type ExternalWalletTarget = "metamask" | "trustwallet";
export type ExternalFinanceHookStatus = "idle" | "connecting" | FinanceSigningStatus | "failed";

/**
 * `wallet_switchEthereumChain` — the same mechanism the withdraw flow and
 * `connect-wallet/page.tsx` use. No `wallet_addEthereumChain` fallback: that
 * call needs RPC URLs and a currency symbol this client has no canonical
 * source for.
 */
async function switchChainOnProvider(provider: Eip1193Provider, chainId: number): Promise<void> {
  await provider.request({
    method: "wallet_switchEthereumChain",
    params: [{
      chainId: `0x${chainId.toString(16)}`
    }]
  });
}
const RECEIPT_POLL_ERROR_STATUS: {
  status: null;
} = {
  status: null
};

/** ethers is imported lazily everywhere in this codebase; same here. */
async function waitForReceiptViaEthers(provider: Eip1193Provider, trxHash: string): Promise<{
  status: number | null;
}> {
  const {
    BrowserProvider
  } = await import("ethers");
  const browserProvider = new BrowserProvider(provider as any);
  try {
    const receipt = await browserProvider.waitForTransaction(trxHash);
    return {
      status: receipt?.status ?? null
    };
  } catch {
    // The node lost track of the tx locally, or the RPC call itself failed —
    // NOT the same as a revert. `trxHash` stays authoritative either way.
    return RECEIPT_POLL_ERROR_STATUS;
  }
}
function isUserRejection(error: unknown): boolean {
  const code = (error as any)?.code;
  return code === 4001 || code === "ACTION_REJECTED" || (error as any)?.info?.error?.code === 4001;
}
export function describeFinanceSigningStatus(status: ExternalFinanceHookStatus, isFa: boolean): string | null {
  switch (status) {
    case "connecting":
      return isFa ? "در حال اتصال به کیف پول..." : "Connecting to your wallet…";
    case "validating_wallet_account":
      return isFa ? "در حال بررسی حساب متصل..." : "Checking the connected account…";
    case "validating_network":
      return isFa ? "در حال بررسی شبکه..." : "Checking the network…";
    case "awaiting_wallet_signature":
      return isFa ? "در انتظار تایید در کیف پول..." : "Waiting for approval in your wallet…";
    case "awaiting_typed_signature":
      // Named apart from the send case on purpose: the wallet shows a Sign
      // popup here, not a Confirm-and-pay one, and a user told to expect a
      // gas fee will hesitate over a free signature.
      return isFa ? "در انتظار امضا در کیف پول (بدون کارمزد)..." : "Waiting for a signature in your wallet (no gas fee)…";
    case "transaction_submitted":
      return isFa ? "تراکنش ارسال شد، در انتظار تایید بلاکچین..." : "Transaction submitted, waiting for on-chain confirmation…";
    case "waiting_for_receipt":
      return isFa ? "در انتظار تایید بلاکچین..." : "Waiting for the blockchain to confirm…";
    case "confirming_backend":
      return isFa ? "در حال تایید نهایی با سرور..." : "Confirming with the server…";
    case "retrying_same_confirmation":
      return isFa ? "سرور هنوز این تراکنش را ثبت نکرده است، در حال تلاش دوباره..." : "The server hasn't indexed this transaction yet — retrying…";
    case "awaiting_next_signature":
      return isFa ? "یک امضای دیگر لازم است؛ کیف پول خود را بررسی کنید." : "One more signature is required — check your wallet.";
    default:
      return null;
  }
}
export function useExternalFinanceSigning(lang: "fa" | "en") {
  const isFa = lang === "fa";
  const [status, setStatus] = useState<ExternalFinanceHookStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [connectedAddress, setConnectedAddress] = useState<string | null>(null);
  const [activeWallet, setActiveWallet] = useState<ExternalWalletTarget | null>(null);
  /**
   * Every hash this attempt produced, set as each one comes back — so the UI
   * can tell "nothing was sent yet" from "a transaction exists, and retrying
   * must never mean sending another one" even when the attempt ends in a
   * thrown error rather than a result.
   */
  const [trxHashes, setTrxHashes] = useState<string[]>([]);
  const connectionRef = useRef<WalletConnection | null>(null);
  const inFlight = useRef(false);
  /**
   * Keyed by STEP, not by round — the one guard that must survive React
   * re-renders, and the reason it is by step is in `sendTransaction`'s own
   * doc: a resumed attempt renumbers the rounds but not the steps.
   */
  const sentHashesRef = useRef<Map<string, string>>(new Map());
  /**
   * The same guard for EIP-712 rounds. Re-signing costs nothing, but it does
   * re-open a wallet popup the user has already dealt with — and on a resumed
   * attempt that popup is indistinguishable from being asked to sign twice.
   */
  const signaturesRef = useRef<Map<string, string>>(new Map());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const safeSet = useCallback(<T,>(setter: (value: T) => void, value: T) => {
    if (mounted.current) setter(value);
  }, []);
  const describeFailure = useCallback((thrown: unknown): string => {
    if (thrown instanceof AccountMismatchError) {
      return isFa ? `کیف پول متصل با آدرس مورد انتظار یکسان نیست. لطفاً در کیف پول خود به آدرس ${thrown.expected} تغییر دهید.` : `The connected wallet does not match the expected address. Please switch to ${thrown.expected} in your wallet.`;
    }
    if (thrown instanceof NetworkMismatchError) {
      return isFa ? `شبکه‌ی کیف پول با شبکه‌ی مورد نیاز (chain id ${thrown.expectedChainId}) یکسان نشد.` : `Your wallet's network did not switch to the required network (chain id ${thrown.expectedChainId}).`;
    }
    if (thrown instanceof TransactionFailedError) {
      return isFa ? "تراکنش در بلاکچین ناموفق بود یا رد شد. هیچ تغییری ثبت نشد." : "The transaction failed or reverted on-chain. Nothing was recorded.";
    }
    if (thrown instanceof MissingSignPayloadError || thrown instanceof UnknownFinanceStepError) {
      return isFa ? "سرور مرحله‌ای برگرداند که این نسخه نمی‌شناسد. لطفاً بعداً دوباره تلاش کنید." : "The server returned a step this version does not recognise. Please try again later.";
    }
    if (thrown instanceof WalletConnectorError) {
      switch (thrown.reason) {
        case "not_installed":
          return isFa ? "کیف پول پیدا نشد. آن را نصب کنید یا این صفحه را در مرورگر کیف پول باز کنید." : "Wallet not found. Install it, or open this page in the wallet's own browser.";
        case "rejected":
          return isFa ? "درخواست در کیف پول رد شد." : "The request was rejected in your wallet.";
        case "no_accounts":
          return isFa ? "هیچ حسابی در کیف پول انتخاب نشده است." : "No account is selected in your wallet.";
        default:
          return isFa ? "اتصال به کیف پول ناموفق بود." : "Connecting to the wallet failed.";
      }
    }
    if (isUserRejection(thrown)) {
      return isFa ? "درخواست در کیف پول لغو شد. می‌توانید دوباره تلاش کنید." : "The request was cancelled in your wallet. You can try again.";
    }
    const backendMessage = (thrown as any)?.response?.data?.message || Array.isArray((thrown as any)?.response?.data?.errors) && (thrown as any).response.data.errors[0];
    if (typeof backendMessage === "string" && backendMessage) return backendMessage;
    return isFa ? "امضا با کیف پول خارجی ناموفق بود." : "Signing with your external wallet failed.";
  }, [isFa]);
  const connect = useCallback(async (target: ExternalWalletTarget) => {
    if (inFlight.current) return null;
    setError(null);
    setActiveWallet(target);
    setStatus("connecting");
    try {
      const connection = await connectWallet(target);
      connectionRef.current = connection;
      safeSet(setConnectedAddress, connection.address);
      safeSet(setStatus, "idle");
      return connection;
    } catch (thrown_0) {
      safeSet(setError, describeFailure(thrown_0));
      safeSet(setStatus, "idle");
      if (thrown_0 instanceof WalletConnectorError && thrown_0.reason === "not_installed") {
        const openUrl = getWalletOpenUrl(target);
        try {
          if (openUrl) window.open(openUrl, "_blank", "noopener,noreferrer");
        } catch {
          /* popup blocked — the message above already says what to do */
        }
      }
      return null;
    }
  }, [describeFailure, safeSet]);

  /**
   * Runs every round the backend asks for, once per call. A second call while
   * one is in flight is a no-op, and `sentHashesRef` additionally makes
   * re-sending a step's transaction impossible even if `run` were somehow
   * re-entered after a hash for that step already exists.
   */
  const run = useCallback(async (args: {
    signPayload: FinanceSignPayload;
    step: SignRequiredStep;
    confirmStep: (payload: ConfirmFinanceStepPayload) => Promise<FinanceStepResponse>;
    onStepAdvanced?: (next: {
      step: SignRequiredStep;
      signPayload: FinanceSignPayload;
    }) => void;
  }): Promise<FinanceSigningResult | null> => {
    if (inFlight.current) return null;
    const connection_0 = connectionRef.current;
    if (!connection_0) {
      safeSet(setError, isFa ? "ابتدا یک کیف پول را متصل کنید." : "Connect a wallet first.");
      return null;
    }
    inFlight.current = true;
    safeSet(setError, null);
    try {
      const result = await runExternalFinanceSigning({
        signPayload: args.signPayload,
        step: args.step,
        readAccount: () => connection_0.readAccount(),
        switchNetwork: chainId => switchChainOnProvider(connection_0.provider, chainId),
        sendTransaction: async (transaction, {
          step
        }) => {
          const already = sentHashesRef.current.get(step);
          if (already) return already;
          const hash = (await connection_0.provider.request({
            method: "eth_sendTransaction",
            params: [transaction]
          })) as string;
          sentHashesRef.current.set(step, hash);
          safeSet(setTrxHashes, Array.from(sentHashesRef.current.values()));
          return hash;
        },
        signTypedData: async (typedData, {
          step: step_0
        }) => {
          const already_0 = signaturesRef.current.get(step_0);
          if (already_0) return already_0;
          // `eth_signTypedData_v4` takes the document as a JSON STRING, not
          // as an object — MetaMask and Trust Wallet both reject the object
          // form. The document itself is the backend's, stringified as-is.
          //
          // The signer is read live rather than taken from `args`: on a
          // resumed attempt the round's payload can be a later one, and the
          // account it names is the one that must sign.
          const account = await connection_0.readAccount();
          const signature = (await connection_0.provider.request({
            method: "eth_signTypedData_v4",
            params: [account.address, JSON.stringify(typedData)]
          })) as string;
          signaturesRef.current.set(step_0, signature);
          return signature;
        },
        waitForReceipt: trxHash => waitForReceiptViaEthers(connection_0.provider, trxHash),
        confirmStep: args.confirmStep,
        onStepAdvanced: args.onStepAdvanced,
        onStatus: next => safeSet(setStatus, next)
      });
      // `pending_confirmation` leaves the last status at "confirming…",
      // which would read as still-running forever — and, for callers that
      // gate closing the dialog on `isBusy`, would trap the user in it.
      // Idle is accurate: nothing is in flight, the retry is theirs to make.
      if (result.outcome === "pending_confirmation") safeSet(setStatus, "idle");
      return result;
    } catch (thrown_1) {
      safeSet(setError, describeFailure(thrown_1));
      safeSet(setStatus, "failed");
      return null;
    } finally {
      inFlight.current = false;
    }
  }, [describeFailure, isFa, safeSet]);
  const reset = useCallback(() => {
    connectionRef.current = null;
    sentHashesRef.current = new Map();
    signaturesRef.current = new Map();
    inFlight.current = false;
    setStatus("idle");
    setError(null);
    setConnectedAddress(null);
    setActiveWallet(null);
    setTrxHashes([]);
  }, []);
  return {
    status,
    error,
    connectedAddress,
    activeWallet,
    trxHashes,
    connect,
    run,
    reset,
    isBusy: status !== "idle" && status !== "failed" && status !== "done"
  };
}